var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { bool, num, str } from './helpers';
import { getAutomotiveFontScale } from './themes';
/** Resolve widget box from props — dials use dialSize, panels use width/height. */
export function resolveBounds(props, defaultWidth, defaultHeight, pad) {
    if (pad === void 0) { pad = 8; }
    var width = 'width' in props && typeof props.width === 'number'
        ? Math.max(24, props.width)
        : Math.max(56, num(props, 'width', defaultWidth));
    var height = 'height' in props && typeof props.height === 'number'
        ? Math.max(20, props.height)
        : Math.max(44, num(props, 'height', defaultHeight));
    var adaptivePad = Math.min(pad, Math.max(2, Math.round(Math.min(width, height) * 0.1)));
    var innerWidth = Math.max(16, width - adaptivePad * 2);
    var innerHeight = Math.max(12, height - adaptivePad * 2);
    var maxDial = Math.min(innerWidth, innerHeight);
    var explicit = num(props, 'size', 0);
    var dialSize = explicit > 0 ? Math.min(explicit, maxDial) : Math.max(28, maxDial);
    return { width: width, height: height, pad: adaptivePad, innerWidth: innerWidth, innerHeight: innerHeight, dialSize: dialSize };
}
/** True when widget should use compact LCD layout instead of analog dial. */
export function isCompactBounds(bounds) {
    return bounds.innerWidth < 112 || bounds.innerHeight < 76;
}
/** Estimate rendered text width for automotive monospace/sans labels. */
export function estimateTextWidth(text, fontSize) {
    return Math.max(fontSize, text.length * fontSize * 0.55);
}
/** Estimate left-x so text fits inside a box (avoids center-anchor bounds overflow). */
export function fitTextX(text, fontSize, boxW, pad) {
    if (pad === void 0) { pad = 0; }
    var estW = Math.min(boxW - pad * 2, estimateTextWidth(text, fontSize));
    return pad + Math.max(0, (boxW - pad * 2 - estW) / 2);
}
/** Shrink font until text fits inside a box; returns centered left-x. */
export function fitFontSizeToWidth(text, boxW, maxSize, minSize, pad) {
    if (minSize === void 0) { minSize = 6; }
    if (pad === void 0) { pad = 0; }
    var available = Math.max(8, boxW - pad * 2);
    var fontSize = maxSize;
    while (fontSize > minSize && estimateTextWidth(text, fontSize) > available) {
        fontSize -= 1;
    }
    var estW = Math.min(available, estimateTextWidth(text, fontSize));
    return { fontSize: fontSize, x: pad + Math.max(0, (available - estW) / 2) };
}
/** Convert a desired anchor y into canvas top-y (renderer uses textBaseline top). */
export function textYForBaseline(y, fontSize, baseline) {
    if (baseline === void 0) { baseline = 'middle'; }
    if (baseline === 'middle')
        return y - fontSize * 0.5;
    if (baseline === 'bottom' || baseline === 'ideographic')
        return y - fontSize;
    return y;
}
/** Centered text with stable box for HTML + bounds. */
export function autoCenteredText(app, text, boxW, y, options) {
    var _a, _b, _c, _d, _e, _f;
    if (options === void 0) { options = {}; }
    var fontSize = (_a = options.fontSize) !== null && _a !== void 0 ? _a : 12;
    var insetX = (_b = options.insetX) !== null && _b !== void 0 ? _b : 0;
    var insetY = (_c = options.insetY) !== null && _c !== void 0 ? _c : 0;
    var baseline = (_d = options.textBaseline) !== null && _d !== void 0 ? _d : 'middle';
    return app.text({
        text: text,
        x: insetX + boxW / 2,
        y: insetY + textYForBaseline(y, fontSize, baseline),
        fontSize: fontSize,
        fontWeight: (_e = options.fontWeight) !== null && _e !== void 0 ? _e : 'normal',
        fill: (_f = options.fill) !== null && _f !== void 0 ? _f : '#fff',
        fontFamily: options.fontFamily,
        textAlign: 'center',
        metadata: { textBoxWidth: boxW, textBoxCenterY: insetY + y },
        listening: false,
    });
}
export function fluidFont(base, bounds, min, max) {
    if (min === void 0) { min = 8; }
    if (max === void 0) { max = 24; }
    var scale = Math.min(bounds.innerWidth, bounds.innerHeight) / 120;
    var themeScale = getAutomotiveFontScale();
    return Math.round(Math.min(max, Math.max(min, base * scale * themeScale)));
}
export function centerInBounds(bounds, contentW, contentH) {
    return {
        x: bounds.pad + Math.max(0, (bounds.innerWidth - contentW) / 2),
        y: bounds.pad + Math.max(0, (bounds.innerHeight - contentH) / 2),
    };
}
/** Resolve analog needle dial vs digital LCD readout. */
export function resolveDisplay(props, fallback) {
    if (fallback === void 0) { fallback = 'analog'; }
    var mode = str(props, 'display', '').toLowerCase();
    if (mode === 'digital' || mode === 'lcd')
        return 'digital';
    if (mode === 'analog')
        return 'analog';
    if (str(props, 'theme', '') === 'digital' && bool(props, 'digitalGauges', false)) {
        return 'digital';
    }
    return fallback;
}
/** Proportional instrument-cluster layout — all child widgets scale to cluster bounds. */
export function resolveClusterLayout(rawW, rawH, options) {
    if (options === void 0) { options = {}; }
    // Extreme landscape letterboxes into a cockpit aspect so dials stay round and readable.
    var maxAspect = 2.45;
    var w = rawW;
    var offsetX = 0;
    if (rawH > 0 && rawW / rawH > maxAspect) {
        w = Math.round(rawH * maxAspect);
        offsetX = Math.round((rawW - w) / 2);
    }
    var h = rawH;
    var tiny = w < 140 || h < 90;
    var compact = h < 200;
    var margin = Math.max(tiny ? 4 : 6, Math.min(w, h) * (tiny ? 0.014 : 0.018));
    var short = h < 240;
    var bottomBand = Math.max(short ? 22 : 26, Math.round(h * (short ? 0.12 : 0.14)));
    var bottomY = h - bottomBand - margin * 0.5;
    var topSpace = Math.max(36, bottomY - margin);
    var cx = w / 2;
    var dialSize = Math.max(compact ? 32 : 36, Math.min(w * 0.18, topSpace * (short ? 0.36 : 0.44)));
    var dialBox = dialSize + Math.max(compact ? 4 : 6, dialSize * 0.08);
    var maxDialBox = Math.max(compact ? 36 : 40, (w - margin * 3) / 2);
    if (dialBox > maxDialBox) {
        dialBox = maxDialBox;
    }
    var fittedDialSize = Math.max(compact ? 28 : 40, dialBox - Math.max(compact ? 4 : 6, dialBox * 0.08));
    var smallDial = Math.max(compact ? 22 : 28, Math.min(dialSize * 0.48, w * 0.09, topSpace * 0.2));
    var smallBox = smallDial + Math.max(compact ? 4 : 5, smallDial * 0.08);
    var gearW = Math.max(compact ? 30 : 34, w * 0.065);
    var gearH = compact
        ? Math.max(20, Math.min(24, h * 0.11))
        : Math.max(30, Math.min(h * 0.13, topSpace * 0.2));
    var turnW = Math.max(compact ? 32 : 36, w * 0.065);
    var turnH = compact ? Math.max(11, h * 0.042) : Math.max(14, h * 0.055);
    var fuelW = Math.max(52, w * 0.13);
    var fuelH = Math.max(compact ? 22 : 26, bottomBand * 0.86);
    var batW = Math.max(44, w * 0.1);
    var batH = Math.max(16, bottomBand * 0.55);
    var tpmsW = Math.max(compact ? 72 : 88, w * 0.18);
    var tpmsH = compact
        ? Math.max(40, Math.min(56, h * 0.22))
        : Math.max(56, Math.min(h * 0.2, topSpace * 0.28));
    var lampSize = Math.max(compact ? 14 : 18, Math.min(bottomBand * 0.75, w * 0.036));
    var cruiseW = Math.max(36, w * 0.085);
    var cruiseH = Math.max(compact ? 13 : 16, bottomBand * 0.48);
    var adasW = Math.max(40, w * 0.1);
    var adasH = Math.max(10, bottomBand * 0.4);
    var centerTop = margin + smallBox + (compact ? 2 : 4);
    var centerBottom = bottomY - (compact ? 2 : 4);
    var centerGap = compact ? 3 : 4;
    var centerNeed = gearH + turnH + tpmsH + centerGap * 2;
    var centerAvail = Math.max(24, centerBottom - centerTop);
    if (centerNeed > centerAvail) {
        var scale = centerAvail / centerNeed;
        gearH = Math.max(18, gearH * scale);
        turnH = Math.max(10, turnH * scale);
        tpmsH = Math.max(compact ? 32 : 40, tpmsH * scale);
        centerNeed = gearH + turnH + tpmsH + centerGap * 2;
    }
    var centerSlack = Math.max(0, centerAvail - centerNeed);
    var gearY = centerTop + centerSlack * 0.12;
    var turnY = gearY + gearH + centerGap;
    var tpmsY = turnY + turnH + centerGap;
    var centerY = function (boxH) {
        return Math.min(bottomY + (bottomBand - boxH) / 2, h - margin - boxH);
    };
    var leftUsed = margin + fuelW + margin * 0.35 + batW + margin;
    var rightNeeded = lampSize * 3 + cruiseW + adasW + margin * 1.4;
    var rightAvail = Math.max(40, w - leftUsed - margin - tpmsW * 0.35);
    if (rightNeeded > rightAvail) {
        var scale = rightAvail / rightNeeded;
        lampSize = Math.max(16, lampSize * scale);
        cruiseW = Math.max(36, cruiseW * scale);
        cruiseH = Math.max(14, cruiseH * scale);
        adasW = Math.max(40, adasW * scale);
        adasH = Math.max(10, adasH * scale);
    }
    var rx = w - margin;
    var placeRight = function (type, rw, rh) {
        rx -= rw;
        var x = Math.max(margin, rx);
        var slot = { type: type, x: x, y: centerY(rh), width: rw, height: rh };
        rx = x - margin * 0.28;
        return slot;
    };
    var compactRight = compact && w < 320;
    var rightSlots = compactRight
        ? [placeRight('cruiseControl', cruiseW, cruiseH), placeRight('adasStatus', adasW, adasH)]
        : [
            placeRight('adasStatus', adasW, adasH),
            placeRight('warningLamp', lampSize, lampSize),
            placeRight('cruiseControl', cruiseW, cruiseH),
            placeRight('headlights', lampSize, lampSize),
            placeRight('parkingBrake', lampSize, lampSize),
        ];
    var interGap = Math.max(6, margin * 0.45);
    var callBandW = w - margin * 2 - dialBox * 2 - interGap * 2;
    var showCall = !!options.callScreen && w >= 520 && h >= 220 && callBandW >= 120;
    var centerSlots = [];
    if (showCall) {
        var callH = Math.max(72, Math.min(h * 0.36, dialBox * 1.02, topSpace * 0.58));
        var callY = margin +
            Math.max(0, (dialBox - callH) * 0.42) +
            (compact && h < 176 ? 0 : smallBox * 0.12);
        centerSlots.push({
            type: 'callScreen',
            x: margin + dialBox + interGap,
            y: callY,
            width: callBandW,
            height: callH,
        });
        var bottomGearY = bottomY + (bottomBand - gearH) / 2;
        var gearX = Math.max(margin + dialBox + interGap, cx - gearW - turnW / 2 - 4);
        centerSlots.push({ type: 'gearIndicator', x: gearX, y: bottomGearY, width: gearW, height: gearH }, {
            type: 'turnIndicators',
            x: Math.min(w - margin - dialBox - interGap - turnW, cx - turnW / 2),
            y: bottomGearY + (gearH - turnH) / 2,
            width: turnW,
            height: turnH,
        });
        // Call overlay owns the center — only show TPMS when there is a readable band
        // below the call (never the old 22–30px strip that looked vertically crushed).
        var tpmsMinReadable = 52;
        var tpmsY2 = callY + callH + 8;
        var tpmsSpace = bottomY - 4 - tpmsY2;
        if (tpmsSpace >= tpmsMinReadable) {
            var tpmsStripH = Math.min(tpmsH, Math.max(tpmsMinReadable, Math.min(tpmsSpace, h * 0.16)));
            centerSlots.push({
                type: 'tpms',
                x: cx - tpmsW / 2,
                y: tpmsY2,
                width: Math.min(tpmsW, callBandW * 0.85),
                height: tpmsStripH,
            });
        }
    }
    else {
        centerSlots.push({ type: 'gearIndicator', x: cx - gearW / 2, y: gearY, width: gearW, height: gearH }, { type: 'turnIndicators', x: cx - turnW / 2, y: turnY, width: turnW, height: turnH }, { type: 'tpms', x: cx - tpmsW / 2, y: tpmsY, width: tpmsW, height: tpmsH });
    }
    var slots = __spreadArray(__spreadArray(__spreadArray(__spreadArray([
        { type: 'speedometer', x: margin, y: margin, width: dialBox, height: dialBox, size: fittedDialSize },
        { type: 'tachometer', x: w - margin - dialBox, y: margin, width: dialBox, height: dialBox, size: fittedDialSize }
    ], __read((compact && h < 176 || showCall
        ? []
        : [{ type: 'engineTemp', x: cx - smallBox / 2, y: margin + 3, width: smallBox, height: smallBox, size: smallDial }])), false), __read(centerSlots), false), [
        { type: 'fuelGauge', x: margin, y: centerY(fuelH), width: fuelW, height: fuelH },
        { type: 'batteryVoltage', x: margin + fuelW + margin * 0.35, y: centerY(batH), width: batW, height: batH }
    ], false), __read(rightSlots), false);
    return slots.map(function (slot) { return (__assign(__assign({}, slot), { x: offsetX + Math.max(margin, Math.min(slot.x, w - margin - slot.width)), y: Math.max(margin, Math.min(slot.y, h - margin - slot.height)) })); });
}
